import React, { useCallback, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import { useHistory } from 'react-router';
import { makeStyles, createStyles, Button } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import Spin from 'components/atoms/Spin/Spin';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import { Order } from 'utils/sort';
import TableSortCell from 'components/atoms/TablePagination/TableSortCell';
import SearchBar from 'components/atoms/SearchBar/SearchBar';
import { useDictionaryApi } from 'api/dictionary';
import { CourierDto, downloadCourierDoc } from 'api/couriers';

const useStyles = makeStyles(() =>
    createStyles({
        tableRow: {
            cursor: 'pointer',
            '&$hover:hover': {
                backgroundColor: '#fff1ad',
            },
            '&$selected, &$selected:hover': {
                backgroundColor: '#ffe35c',
            },
        },
        selected: {},
        hover: {},
    })
);

interface Props {
    url: string;
    route: string;
}

const Couriers = ({ url, route }: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const [currentCourier, setCurrentCourier] = useState<CourierDto | null>(null);
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<CourierDto[]>([]);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = React.useState<Order>('desc');
    const [orderBy, setOrderBy] = React.useState<keyof CourierDto>('id');
    const [search, setSearch] = React.useState<string | null>(null);
    const { getPaged } = useDictionaryApi<CourierDto>(url);

    const handleOpenDetails = (item: CourierDto) =>
        history.push(`/cabinet/courier-${route}/${item.id}`);

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await getPaged(page, perPage, `${orderBy},${order}`, search);
            setItems(x.content);
            setTotal(x.total_elements);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [getPaged, page, perPage, orderBy, order, search, enqueueSnackbar]);

    const handleRequestSort = (property: keyof CourierDto) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleDownload = useCallback(async (item: CourierDto) => {
        try {
            setCurrentCourier(item);
            const response = await downloadCourierDoc(item.id);
            const contentType = response.headers['content-type'];
            const blob = new Blob([response.data], { type: contentType, endings: 'native' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `Договор курьера ${item.last_name} ${item.first_name}.docx`;
            link.click();
        } catch (e) {
        } finally {
            setCurrentCourier(null);
        }
    }, []);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <Box position={'relative'}>
            <SearchBar
                buttonText="Поиск"
                placeholder="Введите текст"
                onButtonClick={() => {}}
                onSearchChange={(event: any) => {
                    setSearch(event.target.value);
                }}
            ></SearchBar>
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'medium'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableSortCell
                                    label={'№'}
                                    orderBy={orderBy}
                                    order={order}
                                    property={'id'}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableCell>Телефон</TableCell>
                                <TableCell>ФИО</TableCell>
                                <TableCell>ИИН</TableCell>
                                <TableCell>Почта</TableCell>
                                <TableCell>Регион</TableCell>
                                <TableCell>Дата создания</TableCell>
                                <TableCell>Скачать договор</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow
                                    key={index}
                                    className={classes.tableRow}
                                    classes={{ selected: classes.selected, hover: classes.hover }}
                                    hover
                                    onDoubleClick={() => handleOpenDetails(item)}
                                    style={{
                                        backgroundColor:
                                            item.is_completed && route === 'activation'
                                                ? '#d4edda'
                                                : '#fff',
                                    }}
                                >
                                    <TableCell>{page * perPage + index + 1}</TableCell>
                                    <TableCell>{item.phone_number}</TableCell>
                                    <TableCell>{`${item.last_name} ${item.first_name}`}</TableCell>
                                    <TableCell>{item.idn}</TableCell>
                                    <TableCell>{item.email}</TableCell>
                                    <TableCell>{item.region?.description}</TableCell>
                                    <TableCell>{item.date_created}</TableCell>
                                    <TableCell>
                                        <Button
                                            disabled={!item.is_completed}
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleDownload(item)}
                                            startIcon={<GetApp />}
                                        >
                                            {currentCourier?.id === item.id && <Spin transparent />}
                                            Скачать
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    total={total}
                    perPage={perPage}
                    page={page}
                    onChangePage={setPage}
                    onChangeRowsPerPage={setPerPage}
                />
            </Box>
        </Box>
    );
};

export default Couriers;
